/**
 * The one way this app talks to the Worker.
 *
 * Every call goes through `request()` so four things are handled once instead
 * of at each call site, where three of them would be forgotten:
 *
 *   1. **401 updates identity.** The session ended somewhere — expired, signed
 *      out in another tab, revoked from settings. Retrying is pointless; the UI
 *      needs to re-render as signed out. `apiFetch` already does this, so we go
 *      through it rather than around it.
 *   2. **The error body is structured.** The Worker answers failures with
 *      `{ error, code }`, and `code` is what the UI branches on —
 *      `storage_exceeded` and `project_read_only` are both 402 and want
 *      completely different words. Throwing a bare `Error("402")` discards the
 *      only part worth having.
 *   3. **429 carries Retry-After.** Surfaced as a number so a caller can say
 *      "try again in 12s" rather than "something went wrong".
 *   4. **Requests can be abandoned.** Autosave fires on a timer; a save the
 *      user has already superseded should be dropped, not awaited.
 */
import { apiFetch } from "../auth/session";

/** Failure shape the Worker actually returns. Never assume it is a string. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Machine-readable reason. Branch on this, never on the message. */
    readonly code: string | null = null,
    /** Seconds, from Retry-After. Only set on 429. */
    readonly retryAfter: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The network never answered — offline, DNS, connection reset. */
  get isOffline(): boolean {
    return this.status === 0;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Wrong plan, out of space, or a locked project. `code` says which. */
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }

  /** Somebody else saved first. The caller must reconcile, not retry. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

export interface RequestOptions {
  method?: string;
  /** JSON-serialised. Mutually exclusive with `bytes`. */
  body?: unknown;
  /** Raw body, for the packed archive upload. */
  bytes?: Uint8Array;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Abandon after this many ms. Autosave should not hang forever on a stalled
   * connection while the user keeps drawing.
   */
  timeoutMs?: number;
}

/** An AbortSignal that knows how to detach the listeners keeping it alive. */
type DisposableSignal = AbortSignal & { dispose?: () => void };

const DEFAULT_TIMEOUT_MS = 20_000;

function retryAfterSeconds(res: Response): number | null {
  const raw = res.headers.get("Retry-After");
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Reads `{ error, code }` if that is what came back, and never throws doing so. */
async function readError(res: Response): Promise<{ message: string; code: string | null }> {
  try {
    const body = (await res.json()) as { error?: unknown; code?: unknown };
    return {
      message: typeof body.error === "string" ? body.error : `HTTP ${res.status}`,
      code: typeof body.code === "string" ? body.code : null,
    };
  } catch {
    // An HTML error page, an empty body, or a proxy in the way. The status is
    // still the useful part.
    return { message: `HTTP ${res.status}`, code: null };
  }
}

/**
 * Perform a request, returning the parsed `Response` on success and throwing
 * `ApiError` on anything else — including the network being gone, which is
 * status 0 rather than an exception type the caller has to know about.
 */
export async function rawRequest(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = "GET", body, bytes, headers = {}, signal, timeoutMs } = options;

  // Compose the caller's signal with our timeout, so either can abandon it.
  const timer = new AbortController();
  const limit = timeoutMs ?? DEFAULT_TIMEOUT_MS;
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    timer.abort();
  }, limit);
  const composed: DisposableSignal = signal ? anySignal([signal, timer.signal]) : timer.signal;
  const release = () => {
    clearTimeout(timeout);
    composed.dispose?.();
  };

  const init: RequestInit = { method, headers, signal: composed };
  if (bytes) {
    init.body = bytes as BodyInit;
    init.headers = { "Content-Type": "application/octet-stream", ...headers };
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { "Content-Type": "application/json", ...headers };
  }

  let res: Response;
  try {
    res = await apiFetch(path, init);
  } catch (err) {
    // A timeout aborts the same signal the caller can abort, so without this
    // flag the two are indistinguishable — and they mean opposite things. A
    // caller cancelling is expected and silent; a request that ran out of time
    // is a failure the user should hear about.
    if (timedOut) throw new ApiError(`request timed out after ${limit}ms`, 0, "timeout");
    if ((err as Error)?.name === "AbortError") throw err;
    // Offline, refused, DNS. Distinct from a served error, and the UI says
    // something different about it — "you are offline, saved locally" rather
    // than "that failed".
    throw new ApiError((err as Error).message || "network unavailable", 0);
  } finally {
    release();
  }

  if (!res.ok) {
    const { message, code } = await readError(res);
    throw new ApiError(message, res.status, code, retryAfterSeconds(res));
  }
  return res;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await rawRequest(path, options);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * AbortSignal.any, with a fallback and a way to clean up after it.
 *
 * Safari shipped `AbortSignal.any` recently enough that relying on it is the
 * sort of thing that fails at runtime on exactly the devices we cannot test on.
 *
 * The `dispose` matters more than it looks. The caller's signal typically lives
 * as long as the component, while a request lives for a second — so a listener
 * added per request and never removed accumulates one entry per autosave for
 * the lifetime of the editor, holding each dead request's controller with it.
 */
function anySignal(signals: AbortSignal[]): DisposableSignal {
  const native = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof native === "function") return native.call(AbortSignal, signals);

  const controller = new AbortController();
  const cleanups: (() => void)[] = [];

  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason);
      break;
    }
    const onAbort = () => controller.abort(s.reason);
    s.addEventListener("abort", onAbort, { once: true });
    cleanups.push(() => s.removeEventListener("abort", onAbort));
  }

  const out = controller.signal as DisposableSignal;
  out.dispose = () => {
    for (const off of cleanups) off();
    cleanups.length = 0;
  };
  return out;
}
